//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import React from 'react';
import {
  CardTravel as UseCaseIcon,
  Code as CodeIcon,
  School as SchoolIcon,
  Storage as StorageIcon
} from '@mui/icons-material';
import { loginTourInfo } from './loginTour';
import { dashboardTourInfo } from './dashboardTour';
import { gettingStartedTourInfo } from './gettingStartedTour';
import { TourInfo } from '@/components/Help/tourTypes';
import { autoToursOff } from '@/components/Help/autoTours';

interface TourCategory {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  disabledReason?: string;
}

interface TourCategoryWithCount extends TourCategory {
  tourCount: number;
}

const tourCategories: TourCategory[] = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    description: 'Basic tours for new users',
    icon: <SchoolIcon />
  },
  {
    id: 'advanced',
    title: 'Advanced Features',
    description: 'Learn about advanced functionality',
    icon: <CodeIcon />,
  },
  {
    id: 'data',
    title: 'Data Management',
    description: 'Working with data sources and datasets',
    icon: <StorageIcon />,
  },
  {
    id: 'use-cases',
    title: 'Use Case Tutorials',
    description: 'Guided walkthroughs for common use cases',
    icon: <UseCaseIcon />,
  }
];

const unauthenticatedTours: TourInfo[] = [
  loginTourInfo
];

const authenticatedTours: TourInfo[] = [
  gettingStartedTourInfo,
  dashboardTourInfo,
];

/**
 * Get available tours based on authentication status.
 * @param isAuthenticated - Whether a user is currently signed in.
 * @returns {Array} - Tours available based on whether user is authenticated.
 */
export const getAvailableTours = (isAuthenticated: boolean): TourInfo[] => {
  return isAuthenticated ? authenticatedTours : unauthenticatedTours;
};

/**
 * Get all tour categories.
 * @returns {Array} Array of tour categories with computed tourCount, filtered
 *                  to only include non-empty categories.
 */
export const getTourCategories = (
  isAuthenticated: boolean
): TourCategoryWithCount[] => {
  return tourCategories
    .map(category => ({
      ...category,
      tourCount: getAvailableTours(isAuthenticated).filter(
                   tour => tour.category === category.id).length
    }))
    .filter(category => category.tourCount > 0);
};

/**
 * Get tour info by ID.
 * @param {string} tourId The ID of the tour.
 * @returns {Object|null} Tour information or null if not found.
 */
export const getTourInfo = (
  tourId: string, isAuthenticated: boolean
): TourInfo | null => {
  return getAvailableTours(isAuthenticated).find(
           tour => tour.id === tourId) || null;
};

/**
 * Whether this tour can still arrive without the user asking for it.
 *
 * The one question the starter and the tooltip's leave gate both have to
 * answer, and they must answer it the same way: the starter decides whether
 * to open the tour on the user's behalf, and the gate decides whether
 * leaving is worth asking about, which is worth doing only while a later
 * run can still come uninvited. Three ways it cannot -- the switch against
 * self-opening surfaces is set, the tour never starts itself, or the user
 * has already answered by finishing it or asking not to see it again.
 *
 * Takes the tour rather than its id so the answer depends on nothing but
 * its arguments: a caller that already holds the tour passes it straight in.
 */
export const canStartItself = (
  tourInfo: TourInfo | null, optedOutTours: string[]
): boolean =>
  !autoToursOff() &&
  Boolean(tourInfo?.autoStarts) &&
  !optedOutTours.includes(tourInfo?.id ?? '');

/**
 * Get tours by category.
 * @param {string} categoryId - Category ID.
 * @returns {Array} - Tours in the category.
 */
export const getToursByCategory = (
  categoryId: string, isAuthenticated: boolean
): TourInfo[] => {
  return getAvailableTours(isAuthenticated).filter(
           tour => tour.category === categoryId);
};

// Validates that the tour is defined correctly and generate errors for
// mistakes.  This is intended to make sure developers define tours correctly.
// The errors should only be displayed when built in development mode.
export const validateTour = (tourInfo: TourInfo | null): string[] => {
  if (!tourInfo || import.meta.env.VITE_MC_BUILD_TYPE !== 'dev')
    return [];

  const requiredTourProps = ['id', 'title', 'description', 'icon', 'steps'];
  const requiredStepProps = ['target', 'title', 'content'];
  const errors: string[] = [];

  // Validate that the properties required for each tour exist.
  requiredTourProps.forEach((prop) => {
    if (!(prop in tourInfo))
      errors.push(`Tour ${tourInfo.id} missing required property: ${prop}`);
  });

  if (tourInfo.steps) {
    // Validate that the properties required for each step exist.
    tourInfo.steps.forEach((step, index) => {
      requiredStepProps.forEach((prop) => {
        if (!(prop in step))
          errors.push(`Step ${index} missing required property: ${prop}`);
      });
    });
  }

  if (tourInfo.branches) {
    for (const [name, info] of Object.entries(tourInfo.branches)) {
      if (tourInfo.steps) {
        // Validate the step array contains at least one step (usually a
        // placeholder) with a branch property for each branch name.
        if (!tourInfo.steps.some((step) => step?.branch === name))
          errors.push(`Branch ${name} doesn't have placeholder step(s).`);
      }

      // Validate that currentBranch() is defined for each top-level branch.
      if (!('currentBranch' in info))
        errors.push(`Branch ${name} missing required property: currentBranch`);

      for (const [branchName, steps] of Object.entries(info)) {
        // Each property is a name and set of steps for a different branch
        // option except for currentBranch.  Skip currentBranch.
        if (branchName === 'currentBranch')
          continue;

        if (!Array.isArray(steps))
          continue;

        // Validate that the properties required for each branch step exist.
        steps.forEach((step, index) => {
          requiredStepProps.forEach((prop) => {
            if (!(prop in step))
              errors.push(`Branch ${name} -> ${branchName} -> step ${index} ` +
                          `missing required property: ${prop}`);
          });
        });
      }
    }
  }

  return errors;
};
